import { useState } from 'react';
import type { AssistantChange } from '../../types/api';
import { undoFailureMessage } from './changeRefresh';

/** Lucide `check` from the vendored font (codepoint from its cmap). */
const GLYPH_CHECK = String.fromCodePoint(0xe06c);

export type UndoChange = (actionId: string) => Promise<AssistantChange>;

interface AssistantChangesProps {
  changes: readonly AssistantChange[];
  /** Absent while the reply is still being written: the lines are read-only then. */
  onUndo?: UndoChange;
  /** Says how an undo went, for the conversation's live region. */
  onAnnounce?: (text: string) => void;
}

/**
 * What the assistant changed on a turn, one plain line each: a check, the
 * summary, and **Undo** while the server still offers it. An undone change
 * reads "Undone"; a failed undo keeps the server's sentence under its line; a
 * change that never landed is muted and has nothing to undo.
 */
export function AssistantChanges({ changes, onUndo, onAnnounce }: AssistantChangesProps) {
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});

  if (changes.length === 0) return null;

  async function undo(change: AssistantChange) {
    if (!onUndo || pending.has(change.action_id)) return;
    const id = change.action_id;
    setPending((current) => new Set(current).add(id));
    setErrors((current) => {
      const { [id]: _dropped, ...rest } = current;
      return rest;
    });
    try {
      await onUndo(id);
      onAnnounce?.(`Undone: ${change.summary}`);
    } catch (caught) {
      const message = undoFailureMessage(caught);
      setErrors((current) => ({ ...current, [id]: message }));
      onAnnounce?.(`Could not undo: ${message}`);
    } finally {
      setPending((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  }

  return (
    <ul className="assistant-changes" aria-label="What it changed">
      {changes.map((change) => {
        const isPending = pending.has(change.action_id);
        const error = errors[change.action_id];
        const lineClass = [
          'assistant-change',
          change.status === 'undone' ? 'assistant-change--undone' : '',
          change.status === 'failed' ? 'assistant-change--failed' : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <li className={lineClass} key={change.action_id}>
            <div className="assistant-change__line">
              {change.status === 'failed' ? null : (
                <span className="assistant-change__glyph" aria-hidden="true">
                  {GLYPH_CHECK}
                </span>
              )}
              <span className="assistant-change__summary">
                {change.status === 'failed' ? (
                  <span className="visually-hidden">Did not save: </span>
                ) : null}
                {change.summary}
              </span>
              {change.status === 'undone' ? (
                <span className="assistant-change__state">Undone</span>
              ) : change.status === 'done' && change.undoable && onUndo ? (
                <button
                  className="assistant-change__undo"
                  type="button"
                  // aria-disabled rather than disabled, so focus stays on the line.
                  aria-disabled={isPending}
                  aria-label={`Undo: ${change.summary}`}
                  onClick={() => void undo(change)}
                >
                  {isPending ? 'Undoing' : 'Undo'}
                </button>
              ) : null}
            </div>
            {error ? <p className="assistant-change__error">{error}</p> : null}
          </li>
        );
      })}
    </ul>
  );
}
